import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm } from "react-hook-form";
import { createKycPrivSchema, KycPrivData } from "../KycPrivForm.schema";
import { KycPrivMode } from "../KycPrivForm.types";
import { QueryProfile } from "@/api/queries/types";
import { Contact } from "@/api/types";

// -- Profile lookup helpers ---------------------------------------------------

type ProfileMap = Record<string, string | number | boolean | null>;

function buildProfileMap(rows: QueryProfile[]): ProfileMap {
  const map: ProfileMap = {};
  for (const p of rows) {
    let value: string | number | boolean | null = null;
    switch (p.primaryType ?? "") {
      case "java.lang.Boolean":
        value = p.booleanValue;
        break;
      case "java.lang.Double":
        value = p.doubleValue;
        break;
      case "java.lang.Integer":
      case "java.lang.Long":
        value = p.intValue;
        break;
      default:
        value = p.stringValue ?? null;
    }
    map[p.attributeKey] = value;
    // Index by short key (strip "contact.XXX." namespace prefix)
    const short = p.attributeKey.replace(/^contact\.[^.]+\./, "");
    if (short !== p.attributeKey) map[short] = value;
    // Also index without type prefix, e.g. "string_mifidEsgPreference" -> "mifidEsgPreference"
    const shortNoType = short.replace(/^[a-z]+_/, "");
    if (shortNoType !== short) map[shortNoType] = value;
  }
  return map;
}

const pStr = (map: ProfileMap, key: string) => String(map[key] ?? "");
const pBool = (map: ProfileMap, key: string) => Boolean(map[key] ?? false);

/** If the stored value starts with "CUSTOM-", returns "other"; otherwise the value as-is. */
const pCustomSel = (map: ProfileMap, key: string): string => {
  const v = pStr(map, key);
  return v.startsWith("CUSTOM-") ? "other" : v;
};
/** Returns the custom text after stripping the "CUSTOM-" prefix, or "" if not a custom value. */
const pCustomText = (map: ProfileMap, key: string): string => {
  const v = pStr(map, key);
  return v.startsWith("CUSTOM-") ? v.slice(7) : "";
};

// -- Default values ----------------------------------------------------------

function buildDefaultValues(
  contact: Contact | null | undefined,
  profileData: QueryProfile[]
): KycPrivData {
  const p = buildProfileMap(profileData);
  const addr = contact?.primaryAddress;

  return {
    // -- Personopplysninger
    personId: contact?.contactId ?? "",
    personName: contact?.name ?? "",
    address: addr?.address1 ?? "",
    zip: addr?.zipCode ?? "",
    city: addr?.city ?? "",
    phone: addr?.phone1 ?? "",
    email: addr?.email ?? "",
    taxCountry: contact?.taxCountry?.code ?? "",
    nationality: contact?.nationality?.code ?? "",
    pep: pStr(p, "amlContactPersonPep"),
    pepCustom: "",

    // -- AML/KYC
    amlEmploymentSituation: pCustomSel(p, "amlEmploymentSituation"),
    amlEmploymentSituationCustom: pCustomText(p, "amlEmploymentSituation"),
    amlPurposeOfCustomerRelationship: pCustomSel(
      p,
      "amlPurposeOfCustomerRelationship"
    ),
    amlPurposeOfCustomerRelationshipCustom: pCustomText(
      p,
      "amlPurposeOfCustomerRelationship"
    ),
    amlInvestedPortfolio: pStr(p, "amlInvestedPortfolio"),
    amlAnnualDeposits: pStr(p, "amlAnnualDeposits"),
    amlSourceOfFunds: pCustomSel(p, "amlSourceOfFunds"),
    amlSourceOfFundsCustom: pCustomText(p, "amlSourceOfFunds"),
    amlSourceOfWealth: pCustomSel(p, "amlSourceOfWealth"),
    amlSourceOfWealthCustom: pCustomText(p, "amlSourceOfWealth"),
    amlManagedOnBehalfOfOthers: pCustomSel(p, "amlManagedOnBehalfOfOthers"),
    amlManagedOnBehalfOfOthersCustom: pCustomText(
      p,
      "amlManagedOnBehalfOfOthers"
    ),
    amlTransfersAbroad: pCustomSel(p, "amlTransfersAbroad"),
    amlTransfersAbroadCustom: pCustomText(p, "amlTransfersAbroad"),
    amlCryptoCurrency: pStr(p, "amlCryptoCurrency"),

    // -- MIFID instruments
    mifidKnowledgeOfMutualFunds: pStr(p, "mifidKnowledgeOfMutualFunds"),
    mifidExperienceOfMutualFunds: pStr(p, "mifidExperienceOfMutualFunds"),
    mifidTradingFrequencyOfMutualFunds: pStr(
      p,
      "mifidTradingFrequencyOfMutualFunds"
    ),
    mifidPeriodOfMutualFunds: pStr(p, "mifidPeriodOfMutualFunds"),
    mifidKnowledgeOfListedStocks: pStr(p, "mifidKnowledgeOfListedStocks"),
    mifidExperienceOfListedStocks: pStr(p, "mifidExperienceOfListedStocks"),
    mifidTradingFrequencyOfListedStocks: pStr(
      p,
      "mifidTradingFrequencyOfListedStocks"
    ),
    mifidPeriodOfListedStocks: pStr(p, "mifidPeriodOfListedStocks"),
    mifidKnowledgeOfUnlistedStocks: pStr(p, "mifidKnowledgeOfUnlistedStocks"),
    mifidExperienceOfUnlistedStocks: pStr(p, "mifidExperienceOfUnlistedStocks"),
    mifidTradingFrequencyOfUnlistedStocks: pStr(
      p,
      "mifidTradingFrequencyOfUnlistedStocks"
    ),
    mifidPeriodOfUnlistedStocks: pStr(p, "mifidPeriodOfUnlistedStocks"),
    mifidKnowledgeOfBonds: pStr(p, "mifidKnowledgeOfBonds"),
    mifidExperienceOfBonds: pStr(p, "mifidExperienceOfBonds"),
    mifidTradingFrequencyOfBonds: pStr(p, "mifidTradingFrequencyOfBonds"),
    mifidPeriodOfBonds: pStr(p, "mifidPeriodOfBonds"),
    mifidKnowledgeOfComplexInvestments: pStr(
      p,
      "mifidKnowledgeOfComplexInvestments"
    ),
    mifidExperienceOfComplexInvestments: pStr(
      p,
      "mifidExperienceOfComplexInvestments"
    ),
    mifidTradingFrequencyOfComplexInvestments: pStr(
      p,
      "mifidTradingFrequencyOfComplexInvestments"
    ),
    mifidPeriodOfComplexInvestments: pStr(p, "mifidPeriodOfComplexInvestments"),
    mifidRelevantExperienceEducation: pStr(
      p,
      "mifidRelevantExperienceEducation"
    ),

    // -- MIFID financial
    mifidTotalNetWealth: pStr(p, "mifidTotalNetWealth"),
    mifidLiquidInvestableWealth: pStr(p, "mifidLiquidInvestableWealth"),
    mifidGrossAnnualIncome: pStr(p, "mifidGrossAnnualIncome"),
    mifidLiquidityBuffer: pStr(p, "mifidLiquidityBuffer"),
    mifidDebtBurden: pStr(p, "mifidDebtBurden"),
    mifidIncomeStability: pStr(p, "mifidIncomeStability"),
    mifidEarliestWithdrawal: pStr(p, "mifidEarliestWithdrawal"),

    // -- MIFID risk and purpose
    mifidMainInvestmentPurpose: pStr(p, "mifidMainInvestmentPurpose"),
    mifidRiskToleranceVolatility: pStr(p, "mifidRiskToleranceVolatility"),
    mifidMarketDropResponse: pStr(p, "mifidMarketDropResponse"),
    mifidLiquidityNeed: pStr(p, "mifidLiquidityNeed"),
    mifidValueDeclineImpact: pStr(p, "mifidValueDeclineImpact"),

    // -- MIFID ESG
    mifidEsgPreference: pStr(p, "mifidEsgPreference"),
    mifidEsgPreferenceType: pStr(p, "mifidEsgPreferenceType"),
  };
}

// -- Hook --------------------------------------------------------------------

export function useKycPrivForm(
  contact?: Contact | null,
  profileData: QueryProfile[] = [],
  mode: KycPrivMode = "combined"
) {
  const schema = useMemo(() => createKycPrivSchema(mode), [mode]);

  const defaultValues = useMemo(
    () => buildDefaultValues(contact, profileData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contact?.id, profileData.length]
  );

  return useForm<KycPrivData>({
    resolver: zodResolver(schema) as Resolver<KycPrivData>,
    values: defaultValues,
  });
}
